import {
  Button,
  Checkbox,
  Checklist,
  FieldGroup,
  Heading,
  LockIcon,
  Select,
  Spacer,
} from 'internalLibrary';
import { ChangeEvent, ChangeEventHandler } from 'react';
import { formatNumberToLocale } from 'formatters/formatNumberToLocale';
import { formatNumberToNumber } from 'formatters/formatNumberToNumber';
import { Formik } from 'formik';
import { RegisterFormValues } from 'types/ui';
import { useAnalytics } from 'use-analytics';
import { useTranslation } from 'react-i18next';
import DoubleInput from 'ui/components/DoubleInput';
import initialValues from './initialValues';
import Input from 'ui/components/Input';
import layout from '../../../../../scss/layout.module.scss';
import removeNonDigits from 'utils/removeNonDigits';
import styles from './RegisterForm.module.scss';
import useRegisterUser from '../useRegisterUser';
import useValidationSchema from './useValidationSchema';

const currencyFilter = (value: string) =>
  formatNumberToNumber(removeNonDigits(value)).toString();
const currencyFormatter = (value: string) =>
  formatNumberToLocale(value, { spaceSymbol: ' ' });

const numberOfChildrenInHouseOptions = [
  {
    name: '...',
    value: '',
  },
  ...Array.from<number, { name: string; value: string }>(
    { length: 11 },
    (_, index) => ({
      name: index.toString(),
      value: index.toString(),
    }),
  ),
];

const numberOfAdultsInHouseOptions = [
  {
    name: '...',
    value: '',
  },
  ...Array.from<number, { name: string; value: string }>(
    { length: 10 },
    (_, index) => ({
      name: (index + 1).toString(),
      value: (index + 1).toString(),
    }),
  ),
];

const RegisterForm = () => {
  const validationSchema = useValidationSchema();
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const {
    handleRegister,
    isSubmitting,
    bankAccountError,
    emailError,
    mobilePhoneError,
    personalIdError,
  } = useRegisterUser();

  const formSubmitted = (values: RegisterFormValues) => {
    handleRegister(values);
  };

  const requirementsIndices = [
    'applicantMustBe18',
    'applicantMustHoldId',
    'applicantMustUndergoCustomaryCreditAssessment',
  ] as const;
  const checklistItems = requirementsIndices.map((idx) => t(idx));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={formSubmitted}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
      }) => {
        const hasError =
          Object.keys(errors).length > 1 ||
          bankAccountError ||
          emailError ||
          mobilePhoneError ||
          personalIdError;
        const valueChanged: ChangeEventHandler<HTMLInputElement> = (e) => {
          const previousValue =
            values[e.target.name as keyof RegisterFormValues];
          track(
            'input',
            {
              description: e.target.name,
              inputPlaceholder: e.target.name,
              value: e.target.value,
            },
            {
              isPasted:
                typeof previousValue === 'string' &&
                e.target.value.length - previousValue.length > 1,
            },
          );
        };

        const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
          track('select', {
            description: e.target.name,
            inputPlaceholder: e.target.name,
            value: e.target.value,
          });
          handleChange(e);
        };

        const onCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
          track('checkbox', {
            description: e.target.name,
            inputPlaceholder: e.target.name,
            checked: e.target.checked,
          });
          handleChange(e);
        };

        const onClick = () => {
          track('click', {
            description: 'submit',
          });
        };

        return (
          <form onSubmit={handleSubmit}>
            <div className={layout['width-lg']}>
              <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                <Heading
                  level="h3"
                  text={t('requirementsTitle')}
                  weight={'medium'}
                />
                <Checklist items={checklistItems} />
              </Spacer>
              <Spacer marginTop={'45px'} className={layout['width-md']}>
                <Heading
                  level="h5"
                  text={t('personalDetailsTitle')}
                  weight={'medium'}
                />
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <FieldGroup>
                    <Input
                      data-test={'field_firstName'}
                      label={t('firstName')}
                      name={'firstName'}
                      onChange={valueChanged}
                    />
                    <Input
                      data-test={'field_lastName'}
                      label={t('lastName')}
                      name={'lastName'}
                      onChange={valueChanged}
                    />
                  </FieldGroup>
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <DoubleInput
                    data-test={'field_personalId'}
                    label={t('personalId_label')}
                    preName="prePersonalId"
                    postName="postPersonalId"
                    helperText={t('personalId_helperText')}
                    onChange={valueChanged}
                    externalError={personalIdError}
                  />
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <DoubleInput
                    data-test={'field_bankAccountNumber'}
                    label={t('bankAccountNumber_label')}
                    preName="bankAccountClearingNumber"
                    postName="bankAccountNumber"
                    helperText={t('bankAccountNumber_helperText')}
                    onChange={valueChanged}
                    externalError={bankAccountError}
                  />
                </Spacer>
              </Spacer>
              <Spacer marginTop={'45px'} className={layout['width-md']}>
                <Heading
                  level="h5"
                  text={t('incomeDetailsTitle')}
                  weight={'medium'}
                />
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_monthlyIncome'}
                    label={t('monthlyIncome_label')}
                    name={'monthlyIncome'}
                    helperText={t('monthlyIncome_helperText')}
                    filter={currencyFilter}
                    formatter={currencyFormatter}
                    rightText={t('currency')}
                    rightTextBold
                    onChange={valueChanged}
                  />
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_monthlyOtherLoans'}
                    label={t('monthlyOtherLoans_label')}
                    name={'monthlyOtherLoans'}
                    helperText={t('monthlyOtherLoans_helperText')}
                    filter={currencyFilter}
                    formatter={currencyFormatter}
                    rightText={t('currency')}
                    rightTextBold
                    onChange={valueChanged}
                  />
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_monthlyHousingCosts'}
                    label={t('monthlyHousingCosts_label')}
                    name={'monthlyHousingCosts'}
                    helperText={t('monthlyHousingCosts_helperText')}
                    filter={currencyFilter}
                    formatter={currencyFormatter}
                    rightText={t('currency')}
                    rightTextBold
                    onChange={valueChanged}
                  />
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_monthlyOtherLivingCosts'}
                    label={t('monthlyOtherLivingCosts_label')}
                    name={'monthlyOtherLivingCosts'}
                    helperText={t('monthlyOtherLivingCosts_helperText')}
                    filter={currencyFilter}
                    formatter={currencyFormatter}
                    rightText={t('currency')}
                    rightTextBold
                    onChange={valueChanged}
                  />
                </Spacer>
              </Spacer>
              <Spacer marginTop={'45px'} className={layout['width-md']}>
                <Heading
                  level="h5"
                  text={t('householdDetailsTitle')}
                  weight={'medium'}
                />
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <FieldGroup
                    validationText={
                      touched.numberOfChildrenInHouse
                        ? errors.numberOfChildrenInHouse
                        : ''
                    }
                  >
                    <Select
                      label={t('numberOfChildrenInHouse_label')}
                      name={'numberOfChildrenInHouse'}
                      onChange={onSelect}
                      onBlur={handleBlur}
                      value={values.numberOfChildrenInHouse}
                      options={numberOfChildrenInHouseOptions}
                    />
                  </FieldGroup>
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <FieldGroup
                    validationText={
                      touched.numberOfAdultsInHouse
                        ? errors.numberOfAdultsInHouse
                        : ''
                    }
                  >
                    <Select
                      label={t('numberOfAdultsInHouse_label')}
                      name={'numberOfAdultsInHouse'}
                      onChange={onSelect}
                      onBlur={handleBlur}
                      value={values.numberOfAdultsInHouse}
                      options={numberOfAdultsInHouseOptions}
                    />
                  </FieldGroup>
                </Spacer>
              </Spacer>
              <Spacer marginTop={'45px'} className={layout['width-md']}>
                <Heading
                  level="h5"
                  text={t('contactDetailsTitle')}
                  weight={'medium'}
                />
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_email'}
                    label={t('email_label')}
                    name={'email'}
                    helperText={t('email_helperText')}
                    onChange={valueChanged}
                    externalError={emailError}
                  />
                </Spacer>
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_phone'}
                    label={t('phone_label')}
                    name={'phone'}
                    filter={removeNonDigits}
                    onChange={valueChanged}
                    type="number"
                    externalError={mobilePhoneError}
                  />
                </Spacer>
              </Spacer>
              <Spacer marginTop={'45px'} className={layout['width-md']}>
                <Heading
                  level="h5"
                  text={t('createPasswordTitle')}
                  weight={'medium'}
                />
                <Spacer marginBottom={{ default: '30px', s: '19px' }}>
                  <Input
                    data-test={'field_password'}
                    label={t('password_label')}
                    name={'password'}
                    helperText={t('password_helperText')}
                    type="password"
                  />
                </Spacer>
              </Spacer>
              <Spacer marginBottom="30px" marginTop="40px">
                <Spacer marginBottom={{ default: '16px', s: '9px' }}>
                  <FieldGroup
                    validationText={touched.agreement1 ? errors.agreement1 : ''}
                  >
                    <div>
                      <Checkbox
                        text={t('agreement1_text')}
                        onChange={onCheckbox}
                        name={'agreement1'}
                        value={values.agreement1}
                        errors={
                          touched.agreement1 && Boolean(errors.agreement1)
                        }
                        dataTest={'field_agreement1'}
                      />
                    </div>
                  </FieldGroup>
                </Spacer>
                <Spacer marginBottom={{ default: '16px', s: '9px' }}>
                  <FieldGroup
                    validationText={touched.agreement2 ? errors.agreement2 : ''}
                  >
                    <div>
                      <Checkbox
                        text={t('agreement2_text')}
                        onChange={onCheckbox}
                        name={'agreement2'}
                        value={values.agreement2}
                        errors={
                          touched.agreement2 && Boolean(errors.agreement2)
                        }
                        dataTest={'field_agreement2'}
                      />
                    </div>
                  </FieldGroup>
                </Spacer>
              </Spacer>

              <div>
                {hasError && (
                  <p className={styles['from-error']}>
                    {t('correctFormErrorsAbove')}
                  </p>
                )}
                <Button
                  leftIcon={<LockIcon />}
                  type="submit"
                  fill={true}
                  className={layout['width-md']}
                  loading={isSubmitting}
                  dataTest={'button_submit'}
                  onClick={onClick}
                >
                  {t('register')}
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default RegisterForm;
